import { Link } from "react-router-dom";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { imageUrl } from "../config";
import { useLang } from "../i18n";

const YACHT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/1bd54f6f-4f89-4274-9cb9-14f0826e9831/images/581409bc9ef14656cbda2700c386d0d26728b769f9310631165f9aee3b4f7fdb.jpeg";
const OCEAN_BG =
  "https://static.prod-images.emergentagent.com/jobs/1bd54f6f-4f89-4274-9cb9-14f0826e9831/images/baaca95f1318b4913cfa2b62fbf9563e299a8f38de00fa9bea4e7d58372afe82.jpeg";
const TEAK_IMG = imageUrl("/api/files/veneer-export/products/91bf43e9-8504-4e60-b50c-8f3d74c396ae.jpg");
const TEAK_LINK = "/kaplama?p=c9b041f6-ac78-4a5b-87cd-cfd5c459c6f3";

export const YachtTeak = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section ref={ref} data-testid="yacht-teak-section" className="relative py-24 md:py-32 overflow-hidden">
      {/* Okyanus arka planı — Amazon bittiği yerde başlar, Zanaat & Doku'dan önce biter */}
      <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden bg-[#0D0C0B]">
        <motion.img
          src={OCEAN_BG}
          alt=""
          style={{ y: bgY }}
          className="w-full h-[125%] object-cover opacity-95 will-change-transform"
        />
        <div className="absolute inset-0 bg-[#0D0C0B]/25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0C0B]/60 via-transparent to-[#0D0C0B]/60" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 md:mb-16 max-w-2xl"
        >
          <div className="inline-block rounded-xl bg-[#0D0C0B]/55 backdrop-blur-sm px-6 py-5 -mx-2">
            <p className="font-sans uppercase tracking-[0.3em] text-xs text-[#F2EFEB] mb-4">{t("yacht.label")}</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tighter text-white leading-[1.05]">
              {t("yacht.headingPre")}<span className="italic text-white/90">{t("yacht.headingItalic")}</span>
            </h2>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {/* Dramatic yacht scene */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-8 relative overflow-hidden rounded-sm group"
          >
            <img
              src={YACHT_IMG}
              alt="Dev dalgaların arasından fırlayan lüks yat"
              className="w-full h-[420px] md:h-[560px] lg:h-[620px] object-cover object-top transition-transform duration-[1200ms] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0B]/70 via-transparent to-transparent" />
            <span className="absolute bottom-5 left-5 font-sans uppercase tracking-[0.2em] text-[10px] text-[#F2EFEB]/90">
              {t("yacht.caption")}
            </span>
          </motion.div>

          {/* Birmanya Teak swatch */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4 flex flex-col"
          >
            <Link
              to={TEAK_LINK}
              data-testid="yacht-teak-image-link"
              className="relative overflow-hidden rounded-sm h-[200px] lg:h-[440px] block group/teak"
            >
              <img
                src={TEAK_IMG}
                alt="Birmanya Teak ahşap kaplama"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-700 group-hover/teak:scale-105"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-sm pointer-events-none" />
            </Link>
            <div className="pt-5">
              <div className="rounded-xl bg-[#0D0C0B]/55 backdrop-blur-sm px-5 py-5">
                <h3 className="font-serif text-2xl text-white mb-2">{t("yacht.teakTitle")}</h3>
                <p className="text-[#F2EFEB]/95 leading-relaxed text-sm mb-5">
                {t("yacht.teakDesc")}
              </p>
              <Link
                to={TEAK_LINK}
                data-testid="yacht-teak-cta"
                className="group inline-flex items-center gap-2.5 font-sans uppercase tracking-[0.15em] text-[11px] text-white border border-white/60 rounded-full px-6 py-3 hover:bg-[#F2EFEB] hover:border-[#F2EFEB] hover:text-[#0D0C0B] transition-all duration-300"
              >
                {t("yacht.cta")}
                <ArrowRight size={14} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
